import type { ReactNode } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { FeatureNotice, FeatureHiddenNotice } from "@/components/app/FeatureNotice";
import { useFeature, type FeatureVisibility } from "@/lib/license";

export type GateOutcome = "loading" | "content" | "notice" | "hidden";

/**
 * Rozhodnutí gate oddělené od renderu, aby šlo testovat bez React Query a routeru.
 *
 * Během načítání se schválně nevrací `hidden`: default-deny by jinak na okamžik
 * problikl „funkce není dostupná" i u povolené stránky.
 */
export function gateOutcome(visibility: FeatureVisibility, isLoading: boolean): GateOutcome {
  if (isLoading) return "loading";
  if (visibility === "ENABLED") return "content";
  if (visibility === "ADVERTISED" || visibility === "LOCKED") return "notice";
  return "hidden";
}

/**
 * Obalí obsah stránky licenční kontrolou. Zrcadlí to, co backend vynucuje
 * na `/admin/api/**` — bez toho se stránka vykreslí a teprve jednotlivé dotazy
 * spadnou na 403 bez vysvětlení.
 *
 * Během načítání se schválně nezobrazuje nic definitivního: default-deny by
 * jinak na okamžik problikl "funkce není dostupná".
 */
export function FeatureGate({ feature, children }: { feature: string; children: ReactNode }) {
  const { visibility, feature: dto, isLoading } = useFeature(feature);

  switch (gateOutcome(visibility, isLoading)) {
    case "loading":
      return (
        <div className="flex justify-center py-12">
          <LoadingIndicator />
        </div>
      );
    case "content":
      return <>{children}</>;
    case "notice":
      return (
        <FeatureNotice
          visibility={visibility as "ADVERTISED" | "LOCKED"}
          featureId={feature}
          featureName={dto?.name}
          advertisingText={dto?.advertisingText}
          plannedVersion={dto?.plannedVersion}
        />
      );
    // HIDDEN nebo feature v odpovědi chybí (default-deny).
    case "hidden":
      return <FeatureHiddenNotice />;
  }
}
